import { operatorTypeOf } from '../../Utils/operatorType';

/**
 * Operator RECORDS → the COMPANIES the office knows.
 *
 * A company that settles in two currencies is two Operator records on
 * purpose — "Golden Transfer- IL's Transport_EUR" and "… _HUF" — because a
 * record carries exactly one `settlement_currency` and that single value is
 * what keeps Month End, payments and invoices in an unambiguous bucket.
 * Each record IS its currency bucket; there is no plural currency field and
 * there must never be one.
 *
 * What ties the pair together is the surviving record's `absorbed_ids`: the
 * ids it answers for. This module reads that link the same way the API's own
 * index does (ph-core `operatorAlias.helper`), so the list on the screen and
 * the scope a portal login gets can never disagree:
 *
 *  - a record naming itself is a no-op, not a group;
 *  - the FIRST claim on an id wins, so two records claiming the same retired
 *    id do not fight over it on every render;
 *  - a record that answers for others is never itself absorbed, so a group is
 *    always one level deep.
 *
 * Grouping is by that explicit link ALONE. Two operators whose names happen
 * to look alike are two suppliers until somebody says otherwise — guessing
 * would merge the money of companies that have nothing to do with each other.
 */

/** The id spelling the operators collection uses, with the legacy fallback. */
export const operatorId = (operator) => String(operator?.Id || operator?.id || '');

/** The two currencies an operator record may settle in. */
export const SETTLEMENT_CURRENCIES = ['EUR', 'HUF'];

/** The currency a record does NOT settle in, or '' when it settles in neither. */
export const otherCurrency = (currency) => {
  const code = String(currency || '').toUpperCase();
  if (!SETTLEMENT_CURRENCIES.includes(code)) return '';
  return SETTLEMENT_CURRENCIES.find((other) => other !== code) || '';
};

const CURRENCY_SUFFIX = /_(EUR|HUF)$/i;

/**
 * 'Golden Transfer- IL's Transport_EUR' → 'Golden Transfer- IL's Transport'.
 *
 * The suffix is the office's own convention for which bucket a record is, so
 * it is noise once the row IS the company. A name that carries no suffix is
 * returned untouched, which is every one of the ~120 single-currency
 * operators.
 */
export const companyName = (name) => {
  const text = String(name ?? '').trim();
  return text.replace(CURRENCY_SUFFIX, '').trim() || text;
};

/** What a new sibling record is called: the company, then its bucket. */
export const siblingName = (name, currency) => `${companyName(name)}_${currency}`;

/** Two names that mean the same company, whatever the currency suffix says. */
const sameCompany = (a, b) => {
  const left = companyName(a).toLowerCase();
  return Boolean(left) && left === companyName(b).toLowerCase();
};

const sameEmail = (a, b) => {
  const left = String(a || '').trim().toLowerCase();
  return Boolean(left) && left === String(b || '').trim().toLowerCase();
};

/**
 * Who answers for whom, read once from the whole collection.
 * Returns `{ absorbedBySurvivor, survivorByAbsorbed }`, both keyed by id.
 */
const readLinks = (rows) => {
  const absorbedBySurvivor = new Map();
  const survivorByAbsorbed = new Map();

  rows.forEach((row) => {
    const id = operatorId(row);
    if (!Array.isArray(row.absorbed_ids)) return;

    const absorbed = [...new Set(row.absorbed_ids.filter(Boolean).map(String))]
      .filter((other) => other !== id)
      .filter((other) => !survivorByAbsorbed.has(other));

    if (!absorbed.length) return;

    absorbedBySurvivor.set(id, absorbed);
    absorbed.forEach((other) => survivorByAbsorbed.set(other, id));
  });

  // One level deep: a record that answers for others answers for itself too,
  // so a claim ON it is dropped rather than building a chain nothing reads.
  [...survivorByAbsorbed.keys()].forEach((absorbedId) => {
    if (!absorbedBySurvivor.has(absorbedId)) return;

    const survivorId = survivorByAbsorbed.get(absorbedId);
    survivorByAbsorbed.delete(absorbedId);
    absorbedBySurvivor.set(
      survivorId,
      (absorbedBySurvivor.get(survivorId) || []).filter((id) => id !== absorbedId),
    );
  });

  return { absorbedBySurvivor, survivorByAbsorbed };
};

const toGroup = (primary, members) => ({
  key: operatorId(primary),
  primary,
  members,
  /**
   * What the row is called.
   *
   * A record still standing on its own keeps its stored name to the letter,
   * suffix and all: it is the only thing telling two unpaired records of the
   * same company apart, and every single-currency operator must read exactly
   * as it did before this existed. The suffix only becomes noise once the
   * row genuinely IS the company and carries both buckets itself.
   */
  name: members.length > 1 ? companyName(primary?.name) : String(primary?.name ?? ''),
  /** The company behind the row, whatever the records happen to be called. */
  company: companyName(primary?.name),
  /** One badge per bucket. Deduped: two records, one currency, one badge. */
  currencies: [...new Set(members.map((member) => member.settlement_currency).filter(Boolean))],
  linked: members.length > 1,
});

/**
 * The collection as companies, in the order the records arrived: a group sits
 * where its first member sat, so an alphabetical list stays alphabetical.
 *
 * A record whose sibling is not in the collection (a deactivated one: the
 * list is the ACTIVE operators) is a group of one. It shows its own currency
 * and nothing is invented for the absent record.
 */
export function groupOperators(operators) {
  const rows = (operators || []).filter((row) => operatorId(row));
  const byId = new Map(rows.map((row) => [operatorId(row), row]));
  const { absorbedBySurvivor, survivorByAbsorbed } = readLinks(rows);

  const seen = new Set();
  const groups = [];

  rows.forEach((row) => {
    const survivorId = survivorByAbsorbed.get(operatorId(row)) || operatorId(row);
    if (seen.has(survivorId)) return;
    seen.add(survivorId);

    const primary = byId.get(survivorId) || row;
    const members = [survivorId, ...(absorbedBySurvivor.get(survivorId) || [])]
      .map((id) => byId.get(id))
      .filter(Boolean);

    groups.push(toGroup(primary, members.length ? members : [primary]));
  });

  return groups;
}

/** The company one record belongs to, within the whole collection. */
export const groupFor = (operator, operators) => {
  const id = operatorId(operator);
  const groups = groupOperators(operators);

  return groups.find((group) => group.members.some((member) => operatorId(member) === id))
    || toGroup(operator, [operator]);
};

/** The screen's search, over every record behind one row. */
export const matchesGroup = (group, { search }) => {
  const query = String(search || '').trim().toLowerCase();
  if (!query) return true;

  return [group.name, group.company, ...group.members.flatMap((member) => [
    member.name, member.email, member.phone_number, member.website, member.settlement_currency,
  ])].some((field) => (field || '').toString().toLowerCase().includes(query));
};

/**
 * Records that could BE the company's other bucket already: an existing row
 * settling in the wanted currency that nothing has claimed yet, matched on
 * the company name or on a shared address. The pairs ops has been running by
 * hand are exactly this — two live records, no link — so adopting one is the
 * common case and minting a third record would be the bug.
 */
export const siblingCandidates = (operator, operators, currency) => {
  const id = operatorId(operator);
  const groups = groupOperators(operators);
  const type = operatorTypeOf(operator);

  return groups
    .filter((group) => !group.linked)
    .map((group) => group.primary)
    .filter((row) => operatorId(row) !== id)
    .filter((row) => row.settlement_currency === currency)
    // A record may only be adopted as the other currency bucket of a record
    // that is the SAME KIND of thing. Adopting a parking bucket as a
    // supplier's second bucket — or the reverse — would put the two under one
    // portal login and one Month End heading, and the match here is on the
    // company NAME, which is exactly what `AU-AA-608 Parkolás` and
    // `AU-AA-608 Shell Tankolás` share.
    .filter((row) => operatorTypeOf(row) === type)
    .filter((row) => sameCompany(row.name, operator?.name)
      || sameEmail(row.email, operator?.email));
};

/**
 * Can this company be given its other settlement currency, and how?
 *
 * `blocked` is the refusal to show the user; it never mentions ids, records
 * or `absorbed_ids`, because the intent being expressed is only "this
 * company also settles in HUF".
 */
export function settlementCurrencyPlan(operator, operators) {
  const group = groupFor(operator, operators);
  const name = group.company || 'This operator';
  const currency = String(operator?.settlement_currency || '').toUpperCase();
  const target = otherCurrency(currency);

  if (!target) {
    return {
      group,
      target: '',
      candidates: [],
      blocked: `${name} has no settlement currency yet. Set EUR or HUF on the operator first.`,
    };
  }

  if (group.currencies.includes(target)) {
    return {
      group, target, candidates: [], blocked: `${name} already settles in ${target}.`,
    };
  }

  // A pair is a pair. A record already answering for another cannot take a
  // third: which of the three a figure belongs to stops being answerable.
  if (group.linked) {
    return {
      group,
      target,
      candidates: [],
      blocked: `${name} is already paired with another record. `
        + `Undo that pairing before adding ${target}.`,
    };
  }

  return {
    group, target, candidates: siblingCandidates(operator, operators, target), blocked: '',
  };
}

/**
 * A 0/1 setting as the API insists on reading it.
 *
 * `active` and the notification flags are validated as integers in 0..1, and
 * a record that came back without one — or with it as a string — would be
 * refused outright. `fallback` is what a missing value means, which for
 * `active` is 1: this screen only ever lists the active operators.
 */
const flag = (value, fallback = 0) => {
  const n = Number(value);
  return n === 0 || n === 1 ? n : fallback;
};

/**
 * The fields the API rereads on every save, spelled the way it demands.
 *
 * `billing` above all: the endpoint reaches into `data.billing.address`
 * without checking, so a record saved without the object at all takes the
 * whole request down with it. An empty email is left out rather than sent as
 * '', which is validated as an address and would refuse the record.
 */
const saveable = (operator) => ({
  email: operator?.email || undefined,
  // Carried, not defaulted. The sibling record of an expense account is
  // another bucket for the same cost in the other currency, never a supplier
  // — and without this line `newSiblingRecord` would mint one that IS a
  // supplier and put it straight back on every assignment picker.
  operator_type: operatorTypeOf(operator),
  active: flag(operator?.active, 1),
  daily_digest_enabled: flag(operator?.daily_digest_enabled),
  monthly_report_enabled: flag(operator?.monthly_report_enabled),
  hide_client_phone_number: flag(operator?.hide_client_phone_number),
  show_driver_fee: flag(operator?.show_driver_fee),
  billing: { ...(operator?.billing || {}) },
});

/**
 * The record to POST for a brand-new bucket: the same company, the same
 * people to contact and the same billing details, settling in the other
 * currency. No `Id` — that is what makes it a create — and no
 * `absorbed_ids`: the surviving record is the one that holds the pairing.
 */
export const newSiblingRecord = (operator, currency) => ({
  ...saveable(operator),
  name: siblingName(operator?.name, currency),
  website: operator?.website || undefined,
  phone_number: operator?.phone_number || undefined,
  settlement_currency: currency,
  active: 1,
});

/**
 * The surviving record, saved with its pairing set.
 *
 * `absorbed_ids` is sent HERE and nowhere else. The API leaves the field
 * alone whenever a request omits it, which is what stops every ordinary save
 * of the operators form from quietly clearing a pairing somebody set on
 * purpose — so this is the one request allowed to carry it.
 */
export const pairedRecord = (operator, siblingId) => ({
  ...operator,
  ...saveable(operator),
  absorbed_ids: [siblingId],
});
